import { useId, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { View } from '@actual-app/components/view';

import { Icono } from '#components/mobile/ui/Icono';
import { color, radio, texto } from '#components/mobile/ui/tokens';

type CampoContrasenaProps = {
  etiqueta: string;
  valor: string;
  onCambio: (valor: string) => void;
  /** Rojo si hay un error y verde si ya está bien; si no, el borde normal. */
  estado?: 'error' | 'bien';
  autoComplete: 'current-password' | 'new-password';
  autoFocus?: boolean;
  /** Debajo del campo: reglas, «Coinciden» o el error. */
  children?: ReactNode;
};

export const estiloCampo = (borde: string): CSSProperties => ({
  height: 52,
  boxSizing: 'border-box',
  width: '100%',
  padding: '0 52px 0 16px',
  backgroundColor: color.surface,
  border: `1.5px solid ${borde}`,
  borderRadius: radio.boton,
  color: color.fg,
  fontSize: 16,
  fontWeight: 500,
  fontFamily: 'inherit',
  outline: 'none',
});

/**
 * Campo de contraseña de Rumbo: oculta con puntos salvo que se toque el ojo
 * (Vadik, 17-oct), se puede pegar y no corrige ni pone mayúsculas.
 */
export function CampoContrasena({
  etiqueta,
  valor,
  onCambio,
  estado,
  autoComplete,
  autoFocus,
  children,
}: CampoContrasenaProps) {
  const { t } = useTranslation();
  const id = useId();
  const [visible, setVisible] = useState(false);
  const borde =
    estado === 'error'
      ? color.bad
      : estado === 'bien'
        ? color.accent
        : color.line;

  return (
    <View style={{ gap: 8 }}>
      <label
        htmlFor={id}
        style={{ ...texto.secundario, fontWeight: 700, color: color.fg2 }}
      >
        {etiqueta}
      </label>
      <View style={{ position: 'relative' }}>
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={valor}
          onChange={e => onCambio(e.target.value)}
          autoComplete={autoComplete}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          // oxlint-disable-next-line jsx-a11y/no-autofocus -- primera acción de la pantalla
          autoFocus={autoFocus}
          aria-invalid={estado === 'error' || undefined}
          style={estiloCampo(borde)}
        />
        <button
          type="button"
          onClick={() => setVisible(v => !v)}
          aria-label={visible ? t('Hide password') : t('Show password')}
          aria-pressed={visible}
          style={{
            position: 'absolute',
            right: 4,
            top: 4,
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 0,
            color: color.fg3,
            cursor: 'pointer',
          }}
        >
          <Icono nombre={visible ? 'eyeoff' : 'eye'} size={20} />
        </button>
      </View>
      {children}
    </View>
  );
}
